import {objectFromEntries} from '@benchling/js-utils';
import produce from 'immer';
import {v4 as uuidv4} from 'uuid';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  MethodNode,
  ProcedureDocumentVersionUpdate,
  ProcedureFlowchart,
  ProcedureWithMetadata,
} from 'procedures/ProcedureDomainTypes';
import {NetworkProcedure} from 'procedures/ProcedurePageComponents';
import {isStaticMethodNode} from 'procedures/processes/MethodNodeUtils';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';

function compareCids(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Node order is defined by method edges, not the order of flowchart.nodes. In the future, branching and
 * loops makes the flowchart.nodes flat list ordering meaningless.
 *
 * This function doesn't attempt to determine the CORRECT order; it simply returns nodes in ANY ORDER
 * that remains consistent across multiple versions of the same flowchart.
 *
 * @returns the given flowchart with nodes sorted into a consistent order
 */
export function normalizeFlowchartOrderingForComparison(flowchart: ProcedureFlowchart): ProcedureFlowchart {
  return {
    ...flowchart,
    nodes: flowchart.nodes.toSorted((a, b) => compareCids(a.cid, b.cid)),
  };
}

export function syncPersistedIdsIntoLocalFlowchart(
  localDraftFlowchart: ProcedureFlowchart,
  cidToIdMap: Record<string, string>
): ProcedureFlowchart {
  return produce(localDraftFlowchart, (draftFlowchart) => {
    // Replace ids on draft nodes using the ids on their matching persisted nodes. New nodes not persisted yet
    // are left alone. Do not sync any other node/step attributes besides ids, so we don't lose user
    // changes since last save.
    for (const node of draftFlowchart.nodes) {
      const refreshedId = cidToIdMap[node.cid];
      // Preserve the existing (null) id for any not-yet-saved method nodes.
      node.id = refreshedId ?? node.id;

      for (const {dataParameterVersion} of node.dataParameterSettings) {
        const refreshedId = cidToIdMap[dataParameterVersion.cid];
        dataParameterVersion.id = refreshedId ?? dataParameterVersion.id;
      }

      for (const {materialInputVersion} of node.materialInputSettings) {
        const refreshedId = cidToIdMap[materialInputVersion.cid];
        materialInputVersion.id = refreshedId ?? materialInputVersion.id;
      }

      for (const {materialOutputVersion} of node.materialOutputSettings) {
        const refreshedId = cidToIdMap[materialOutputVersion.cid];
        materialOutputVersion.id = refreshedId ?? materialOutputVersion.id;
      }

      for (const {equipmentVersion} of node.equipmentSettings) {
        const refreshedId = cidToIdMap[equipmentVersion.cid];
        equipmentVersion.id = refreshedId ?? equipmentVersion.id;
      }

      for (const stepGroup of node.stepGroups) {
        for (const step of stepGroup.steps) {
          const refreshedStepId = cidToIdMap[step.cid];
          // Preserve the existing (null) id for any not-yet-saved steps.
          step.id = refreshedStepId ?? step.id;
        }
      }
    }
  });
}

export function syncPersistedDataIntoLocalFlowchart(
  updatedProcedure: ProcedureWithMetadata,
  idSyncedDraftFlowchart: ProcedureFlowchart,
  documentVersionUpdates: Array<ProcedureDocumentVersionUpdate>
): ProcedureFlowchart {
  const stepCidToDocumentInfo: Record<string, Array<string | null>> = {};
  const methodCidToNewNodeId: Record<string, string> = {};
  for (const node of updatedProcedure.flowchart.nodes) {
    methodCidToNewNodeId[node.cid] = node.nodeId;
    for (const stepGroup of node.stepGroups) {
      for (const step of stepGroup.steps) {
        stepCidToDocumentInfo[step.cid] = [step.documentId, step.fromId];
      }
    }
  }
  return produce(idSyncedDraftFlowchart, (draftFlowchart) => {
    for (const node of draftFlowchart.nodes) {
      // The nodeId is generated on the backend and replaced here on the frontend after the first save
      node.nodeId = methodCidToNewNodeId[node.cid];
      const persistedNode = updatedProcedure.flowchart.nodes.find(({cid}) => cid === node.cid);

      // Parameters can only be edited via the synchronous "submit parameter definitions" flow, so we don't
      // need to worry about rubber-banding UI and can just clobber the draft state with what the backend
      // gives us.
      // This is also necessary because parameters are powered by schemas and fields in the backend and
      // parts of the parameter setting shape end up getting transformed by schemas code in ways the frontend
      // cannot simulate.
      if (persistedNode != null) {
        node.dataParameterSettings = persistedNode.dataParameterSettings;
        node.materialInputSettings = persistedNode.materialInputSettings;
        node.materialOutputSettings = persistedNode.materialOutputSettings;
        node.equipmentSettings = persistedNode.equipmentSettings;
        node.parameterUsages = persistedNode.parameterUsages;
        node.testDefinitions = persistedNode.testDefinitions;
        node.dilutionPlan = persistedNode.dilutionPlan;
      }

      for (const stepGroup of node.stepGroups) {
        for (const step of stepGroup.steps) {
          if (stepCidToDocumentInfo[step.cid] != null) {
            step.documentId = stepCidToDocumentInfo[step.cid][0];
            step.fromId = stepCidToDocumentInfo[step.cid][1];
            if (
              step.documentVersionUpdate != null &&
              documentVersionUpdates.find(
                (update) =>
                  update.documentId === step.documentId &&
                  update.openVersion === step.documentVersionUpdate?.openVersion
              )
            ) {
              // Clear the update if it has already been sent.
              step.documentVersionUpdate = null;
            }
          }
        }
      }
    }
  });
}

/**
 * This function generates an ID-to-CID map for a procedure.
 *
 * NOTE: This function does not need to be refactored to using the PoW latest flowchart version because in PoW
 * procedures we will no longer have to handle state management of the flowchart ourselves, but rather let
 * ProcessFlowchartConfigEditor handle the state management of the flowchart.
 */
export function generateIdToCidMap(procedureGraphQL: NetworkProcedure): Record<string, string> {
  // This is for TypeScript; user-facing messaging is handled elsewhere
  if (isInaccessible(procedureGraphQL.procedureFlowchart.latestFlowchartVersion)) {
    return {};
  }
  const nodeEntries = objectFromEntries(
    procedureGraphQL.procedureFlowchart.latestFlowchartVersion.nodes.map(({id}) => [id, uuidv4()])
  );

  procedureGraphQL.procedureFlowchart.latestFlowchartVersion.nodes.forEach(
    ({
      dataParameterSettings,
      materialInputSettings,
      materialOutputSettings,
      equipmentSettings,
      stepGroups,
    }) => {
      dataParameterSettings.forEach(({version, values}) => {
        const dataParameterVersionCid = uuidv4();
        nodeEntries[version.id] = dataParameterVersionCid;
        values.forEach((parameterValue, i) => {
          nodeEntries[parameterValue.id] = `${dataParameterVersionCid}-planned-value-${i}`;
        });
      });
      materialInputSettings.forEach(({version, values}) => {
        const materialInputVersionCid = uuidv4();
        nodeEntries[version.id] = materialInputVersionCid;
        values.forEach((materialInputValue, i) => {
          nodeEntries[materialInputValue.id] = `${materialInputVersionCid}-planned-value-${i}`;
        });
      });
      materialOutputSettings.forEach(({version}) => {
        const materialOutputVersionCid = uuidv4();
        nodeEntries[version.id] = materialOutputVersionCid;
      });
      equipmentSettings.forEach(({version, values}) => {
        const equipmentVersionCid = uuidv4();
        nodeEntries[version.id] = equipmentVersionCid;
        values.forEach((equipmentValue, i) => {
          nodeEntries[equipmentValue.id] = `${equipmentVersionCid}-planned-value-${i}`;
        });
      });
      stepGroups.forEach(({steps}) => {
        steps.forEach(({id}) => {
          nodeEntries[id] = uuidv4();
        });
      });
    }
  );

  return nodeEntries;
}

export function getDocumentVersionUpdates(
  flowchart: ProcedureFlowchart
): Array<ProcedureDocumentVersionUpdate> {
  return flowchart.nodes.flatMap((node) => getDocumentVersionUpdatesFromMethodNode(node));
}

export function getDocumentVersionUpdatesFromFlowchartConfig(
  flowchartConfig: FlowchartConfigWithMetadata
): Array<ProcedureDocumentVersionUpdate> {
  return flowchartConfig.staticFlowchart.nodesStaticData
    .filter((staticNode) => isStaticMethodNode(staticNode))
    .map(
      (methodTaskSchemaNode) =>
        methodTaskSchemaNode.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData
    )
    .flatMap((methodNode) => getDocumentVersionUpdatesFromMethodNode(methodNode));
}

function getDocumentVersionUpdatesFromMethodNode(
  methodNode: MethodNode
): Array<ProcedureDocumentVersionUpdate> {
  return methodNode.stepGroups.flatMap((stepGroup) =>
    stepGroup.steps
      .filter((step) => step.documentVersionUpdate && step.documentId)
      .map((step) => ({...step.documentVersionUpdate!, documentId: step.documentId!}))
  );
}
